import React from "react";
import {
    DefaultButton,
    IStackStyles,
    Label,
    Panel,
    PanelType,
    ScrollablePane,
    ScrollbarVisibility,
    Stack,
} from "@fluentui/react";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { IAggregatedColumn } from "../../utilities/AggregatedColumnHelper";
import { runWithConcurrency } from "../../utilities/ConcurrencyPool";
import { IAggregatedColumnFilter } from "../Body/useAggregatedDocuments";
import { IAggregatedLibraryDescriptor } from "../Services/AggregatedDocumentsService";
import { FilterItem, FilterSection } from "../../src/taskpane/redux/models/reduxModels";
import { CMFileFilterPanelSection } from "./CMFileFilterPanel/CMFileFilterPanelSection";
import { panelStyles, panelActionButtonStyle, scrollStyle } from "./CMFileFilterPanel/CMFileFilterPanel";
import { escapeXml } from "./CMFileFilterPanel/camlUtils";
import { CancellableSharePointRestApi } from "../../src/taskpane/services/CancellableSharePointRestApi";
import Loader from "../../src/taskpane/components/Loader";
import { buildAggregatedFilterSections, mergeDiscoveredValues, valueLabel } from "./aggregatedFilterSections";
import { fetchLibraryFilterValues } from "../Services/aggregatedFilterValuesService";
import authModule from "../../utilities/AuthModule";
import { logger } from "../../utilities/logger";

/**
 * Cross-library filter panel for the "All documents" view.
 *
 * Renders through the SAME section component as the single-library CMFileFilterPanel
 * (checkbox sections, show more/less, per-section value search) so the two panels
 * cannot drift visually. Differences are confined to the data plumbing: sections are
 * built from MERGED columns (every filterable column shown at once), value
 * suggestions come from the loaded rows, the per-section search fans out across all
 * source libraries via each library's binding, and a checkbox toggle applies
 * immediately through useAggregatedDocuments.buildLibraryWhereXml (CAML per library).
 *
 * The host keeps this panel MOUNTED while the aggregate view lives and only toggles
 * `panelIsOpen` — discovered values, expanded state and per-section search memory
 * must survive close/reopen, or applying a filter (which narrows `rows`) would leave
 * a reopened section with a single checkbox and no way to OR-in a second value.
 */

const FILTER_SEARCH_CONCURRENCY = 4;

// Bounds for the per-section value SEARCH, which pages a Contains query per library.
// Generous enough that a real search still finds its match, finite enough that a broad
// term across a dozen large libraries cannot walk them all.
const SEARCH_PROBE_MAX_PAGES = 2;
const SEARCH_PROBE_MAX_VALUES = 2000;

interface ICMAggregatedFilterPanelProps {
    panelIsOpen: boolean;
    closePanel: () => void;
    mergedColumns: IAggregatedColumn[];
    libraryDescriptors: IAggregatedLibraryDescriptor[];
    rows: any[];
    filters: IAggregatedColumnFilter[];
    applyFilters: (filters: IAggregatedColumnFilter[]) => void;
    isLoading?: boolean;
}

const noFiltersStackStyles: Partial<IStackStyles> = {
    root: { width: "100%", height: "100%", margin: 0, padding: 0, overflow: "auto" }
};

const stackTokens = { childrenGap: 25 };

const sameValue = (a: string, b: string): boolean =>
    (a ?? "").trim().toLowerCase() === (b ?? "").trim().toLowerCase();

export const CMAggregatedFilterPanel: React.FunctionComponent<ICMAggregatedFilterPanelProps> = ({
    panelIsOpen,
    closePanel,
    mergedColumns,
    libraryDescriptors,
    rows,
    filters,
    applyFilters,
    isLoading,
}: ICMAggregatedFilterPanelProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const [sections, setSections] = React.useState<FilterSection[]>(
        () => buildAggregatedFilterSections(mergedColumns, rows, filters, [])
    );

    // Always-current applied filters for event handlers. Selection state is derived
    // from props via an effect (two async hops), so a second toggle landing before
    // the pipeline flushes must not compute from a stale `filters` render value.
    const filtersRef = React.useRef<IAggregatedColumnFilter[]>(filters);
    React.useEffect(() => {
        filtersRef.current = filters;
    }, [filters]);

    React.useEffect(() => {
        // While closed, skip rebuilds (rows churn constantly during scroll); state is
        // kept so discoveries survive. Reopening re-runs this via the dep change.
        if (!panelIsOpen) {
            return;
        }
        setSections(previous => buildAggregatedFilterSections(mergedColumns, rows, filters, previous));
    }, [panelIsOpen, mergedColumns, rows, filters]);

    const hasFilters = sections.some(section => section.items.length > 0);
    const hasAppliedFilters = (filters?.length ?? 0) > 0;

    // Checkbox toggle applies immediately — the aggregate equivalent of the library
    // panel's dispatch + toggleReload. Selection state flows back in via `filters`.
    const onSectionItemChanged = (section: FilterSection, item: FilterItem, isChecked: boolean): void => {
        const currentFilters = filtersRef.current ?? [];
        const columnKey = section.filterField.RealFieldName;
        const itemValue = item.value ?? item.title;
        const existing = currentFilters.find(filter => filter.columnKey === columnKey);
        const withoutItem = (existing?.values ?? []).filter(value => !sameValue(value, itemValue));
        const values = isChecked ? [...withoutItem, itemValue] : withoutItem;
        const others = currentFilters.filter(filter => filter.columnKey !== columnKey);
        const next = values.length > 0
            ? [...others, { columnKey, displayName: section.filterField.DisplayName, values }]
            : others;
        filtersRef.current = next;
        applyFilters(next);
    };

    // The section's server value search reports its merged list here. UNION the new
    // titles into the live items instead of replacing wholesale: the reported list was
    // built from the items captured when the search started, so a wholesale replace
    // would revert a checkbox toggled (or a value discovered) while the search was in
    // flight. Selected flags are owned by the filters-driven rebuild.
    const onSectionItemsReplaced = (section: FilterSection, items: FilterItem[]): void => {
        setSections(current => current.map(existing => {
            if (existing.filterField.RealFieldName !== section.filterField.RealFieldName) {
                return existing;
            }
            const known = new Set(existing.items.map(item => (item.value ?? item.title).trim().toLowerCase()));
            const additions = items.filter(item => {
                const key = (item.value ?? item.title).trim().toLowerCase();
                if (!key || known.has(key)) return false;
                known.add(key);
                return true;
            }).map(item => ({ ...item, selected: false }));
            if (additions.length === 0) {
                return existing;
            }
            const merged = [...existing.items, ...additions]
                .sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" }));
            return { ...existing, items: merged };
        }));
    };

    const onSectionToggleShowMore = (section: FilterSection): void => {
        setSections(current => current.map(existing =>
            existing.filterField.RealFieldName === section.filterField.RealFieldName
                ? { ...existing, showMoreExpanded: !existing.showMoreExpanded }
                : existing));
    };

    // Live probe instances so superseded/abandoned searches can be cancelled — the
    // service otherwise pages a Contains query to exhaustion per library.
    const activeProbesRef = React.useRef<{ api: CancellableSharePointRestApi; webUrl: string }[]>([]);
    const cancelActiveProbes = React.useCallback((): void => {
        for (const probe of activeProbesRef.current) {
            try { probe.api.cancel(probe.webUrl); } catch { /* cancellation is best-effort */ }
        }
        activeProbesRef.current = [];
    }, []);
    React.useEffect(() => cancelActiveProbes, [cancelActiveProbes]);

    // Cross-library twin of getSharepointFilterValues: probe every library that has a
    // binding for the column and merge the distinct values. The service never throws
    // (returns []), and the pool never rejects, so one failing library cannot sink the
    // search. The value is XML-escaped here because the legacy service interpolates it
    // raw into ViewXml (its single-directory callers share that quirk). Results are
    // coerced through valueLabel — user/lookup/taxonomy cells arrive as objects/arrays
    // and would otherwise poison the section merge's string handling.
    const searchColumnValues = React.useCallback(async (columnKey: string, searchValue: string): Promise<string[]> => {
        const column = (mergedColumns ?? []).find(candidate => candidate.key === columnKey);
        if (!column) {
            return [];
        }
        const targets = (libraryDescriptors ?? [])
            .map(descriptor => ({ descriptor, binding: column.bindings?.[descriptor.listId] }))
            .filter(target => !!target.binding);
        const safeValue = escapeXml(searchValue);
        cancelActiveProbes();
        const probes: { api: CancellableSharePointRestApi; webUrl: string }[] = [];
        const results = await runWithConcurrency(
            targets,
            async ({ descriptor, binding }) => {
                const api = new CancellableSharePointRestApi(descriptor.webUrl);
                const probe = { api, webUrl: descriptor.webUrl };
                probes.push(probe);
                activeProbesRef.current.push(probe);
                // scopeRecursiveAll: the aggregate grid is Scope="RecursiveAll"; the
                // probe must see foldered documents too or most values are missed.
                // Bounded here and nowhere else: this runs across N libraries at once,
                // and the underlying probe otherwise pages each of them to exhaustion.
                // The single-library call sites pass no limits and are unaffected.
                return api.getSharepointFilterValues(
                    descriptor.webUrl, descriptor.listId, "", binding.internalName, safeValue, true,
                    { maxPages: SEARCH_PROBE_MAX_PAGES, maxValues: SEARCH_PROBE_MAX_VALUES }
                );
            },
            { concurrency: FILTER_SEARCH_CONCURRENCY }
        );
        activeProbesRef.current = activeProbesRef.current.filter(active => !probes.includes(active));
        const merged = new Set<string>();
        for (const result of results) {
            if (result.status === "fulfilled") {
                for (const rawCell of result.value ?? []) {
                    valueLabel(rawCell).forEach(label => merged.add(label));
                }
            }
        }
        return Array.from(merged);
    }, [mergedColumns, libraryDescriptors, cancelActiveProbes]);

    // Real distinct values from the server, fetched lazily per column the first time the
    // user asks to see that section's values (opens its search box, or expands it).
    //
    // Lazy and not on open: the panel deliberately renders EVERY filterable column at
    // once, so eager discovery would be (columns × libraries) requests on open for
    // columns nobody touches. Once per column per session — the service caches per
    // (list, field), and this ref stops a repeated toggle refiring the fan-out while the
    // first one is still in flight.
    const discoveredColumnsRef = React.useRef<Set<string>>(new Set());

    const discoverColumnValues = React.useCallback((columnKey: string): void => {
        if (discoveredColumnsRef.current.has(columnKey)) {
            return;
        }
        const column = (mergedColumns ?? []).find(candidate => candidate.key === columnKey);
        if (!column) {
            return;
        }
        const targets = (libraryDescriptors ?? [])
            .map(descriptor => ({ descriptor, binding: column.bindings?.[descriptor.listId] }))
            .filter(target => !!target.binding);
        if (targets.length === 0) {
            return;
        }
        discoveredColumnsRef.current.add(columnKey);

        (async () => {
            try {
                const accessToken = await authModule.getSharePointAccessToken();
                const results = await runWithConcurrency(
                    targets,
                    ({ descriptor, binding }) => fetchLibraryFilterValues(
                        accessToken, descriptor.webUrl, descriptor.listId, binding.internalName),
                    { concurrency: FILTER_SEARCH_CONCURRENCY }
                );

                const discovered: string[] = [];
                for (const result of results) {
                    if (result.status === "fulfilled") {
                        for (const rawCell of result.value ?? []) {
                            valueLabel(rawCell).forEach(label => discovered.push(label));
                        }
                    }
                }
                if (discovered.length === 0) {
                    // Nothing came back (every library failed, or the field genuinely has
                    // no values). Re-arm so a later attempt is not silently suppressed.
                    discoveredColumnsRef.current.delete(columnKey);
                    return;
                }

                // Merged into this section's items, which buildAggregatedFilterSections
                // carries forward through its `previous` union — so the discovery
                // survives the next rows/filters rebuild, and its MAX_SECTION_ITEMS valve
                // still applies (selected values are never trimmed).
                setSections(current => current.map(section => section.filterField.RealFieldName !== columnKey
                    ? section
                    : mergeDiscoveredValues(section, discovered)));
            } catch (error) {
                discoveredColumnsRef.current.delete(columnKey);
                logger(error, "CMAggregatedFilterPanel.tsx (discoverColumnValues)");
            }
        })();
    }, [mergedColumns, libraryDescriptors]);

    const visibleSections = sections.filter(section => section.items.length > 0);

    return (
        <Panel
            headerText={languageStrings.Filter}
            isOpen={panelIsOpen}
            onDismiss={closePanel}
            isLightDismiss={true}
            closeButtonAriaLabel="Close"
            type={PanelType.custom}
            customWidth={'350px'}
            styles={panelStyles}
        >
            <div className={scrollStyle}>
                <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                    <div className="xb-content" style={{ marginLeft: 20 }}>
                        {!hasFilters && isLoading && <Loader label={languageStrings.LoadingFilters} />}
                        {!hasFilters && !isLoading && <Stack verticalAlign="center" horizontalAlign="center" styles={noFiltersStackStyles}>
                            <Stack.Item align="center">
                                <Label disabled>{languageStrings.NoFiltersAvailable}</Label>
                            </Stack.Item>
                        </Stack>}
                        {hasFilters && <Stack tokens={stackTokens}>
                            {visibleSections.map(section => (
                                <Stack.Item key={section.filterField.RealFieldName}>
                                    <CMFileFilterPanelSection
                                        filterSections={visibleSections}
                                        filterSection={section}
                                        isInOneDrive={false}
                                        serverRelPath={""}
                                        oneDriveRootDetails={undefined}
                                        currentDirectory={undefined}
                                        isInContentManagerMode={false}
                                        setFilterSectionItem={onSectionItemChanged}
                                        setFilterSectionItems={onSectionItemsReplaced}
                                        setFilterSectionToggleShowMore={onSectionToggleShowMore}
                                        searchFilterValues={searchColumnValues}
                                        onRequestFilterValues={discoverColumnValues}
                                    />
                                </Stack.Item>
                            ))}
                        </Stack>}
                    </div>
                </ScrollablePane>
            </div>
            {(hasFilters || hasAppliedFilters) && <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 15 }} style={{ paddingRight: 10 }}>
                <DefaultButton
                    styles={panelActionButtonStyle}
                    iconProps={{ iconName: 'Refresh' }}
                    disabled={!hasAppliedFilters}
                    onClick={() => {
                        filtersRef.current = [];
                        applyFilters([]);
                    }}
                    text={languageStrings.Reset}
                />
            </Stack>}
        </Panel>
    );
};
